import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Printer, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function OrderPhotoPrintDialog({ open, onOpenChange, fileId, previewUrl }) {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [mockMode, setMockMode] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const [form, setForm] = useState({
    customer_name: '',
    customer_email: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
    phone: '',
  });

  useEffect(() => {
    if (!open) return;
    setSuccess(null);
    setSelected(null);
    setForm((f) => ({
      ...f,
      customer_name: user?.name || '',
      customer_email: user?.email || '',
    }));
    (async () => {
      setLoadingProducts(true);
      try {
        const { data } = await axios.get(`${API}/prints/photo-products`);
        setProducts(data.products || []);
        setMockMode(!!data.mock_mode);
        if ((data.products || []).length > 0) setSelected(data.products[0]);
      } catch (err) {
        toast.error('Could not load print catalog');
      } finally {
        setLoadingProducts(false);
      }
    })();
  }, [open, user]);

  const submit = async () => {
    if (!selected) return toast.error('Select a print size');
    const required = ['customer_name', 'customer_email', 'address_line1', 'city', 'postal_code', 'country'];
    for (const k of required) {
      if (!form[k]) return toast.error(`Missing ${k.replace('_', ' ')}`);
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/prints/photo-orders`, {
        file_id: fileId,
        product_uid: selected.product_uid,
        quantity: 1,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        shipping: {
          address_line1: form.address_line1,
          address_line2: form.address_line2,
          city: form.city,
          state: form.state,
          postal_code: form.postal_code,
          country: form.country,
          phone: form.phone,
        },
      });
      if (data.checkout_url) {
        // Stripe-mediated payment — redirect to hosted checkout
        window.location.href = data.checkout_url;
        return;
      }
      // Fallback (no Stripe configured): show success modal as before
      setSuccess(data);
      toast.success(data.mock_mode ? 'Order placed (preview mode)' : 'Print order placed!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Order failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl" data-testid="order-photo-print-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="w-5 h-5 text-teal-600" /> Print this photo
          </DialogTitle>
          <DialogDescription>
            Get this photo printed and mailed to your door.
            {mockMode && (
              <span className="block mt-2 text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded">
                Preview mode — Gelato API key not configured. Orders are recorded but not fulfilled.
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="text-center py-8 space-y-3" data-testid="photo-order-success">
            <div className="mx-auto w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center">
              <CheckCircle2 className="w-8 h-8 text-teal-600" />
            </div>
            <h3 className="text-lg font-semibold">Print order placed</h3>
            <p className="text-sm text-stone-600">
              Reference: <span className="font-mono">{success.order_reference_id}</span>
            </p>
            <Button onClick={() => onOpenChange(false)} className="bg-teal-600 hover:bg-teal-700 text-white rounded-full">
              Done
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] gap-4">
              {previewUrl && (
                <div className="aspect-square rounded-2xl overflow-hidden bg-stone-100 border border-stone-200">
                  <img src={previewUrl} alt="To print" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-stone-700">Choose a size</Label>
                {loadingProducts ? (
                  <div className="flex items-center gap-2 text-stone-500 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading…
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="print-product-list">
                    {products.map((p) => {
                      const active = selected?.product_uid === p.product_uid;
                      return (
                        <button
                          type="button"
                          key={p.product_uid}
                          onClick={() => setSelected(p)}
                          className={`w-full text-left rounded-xl border p-3 transition flex items-center justify-between ${
                            active
                              ? 'border-teal-500 ring-2 ring-teal-200 bg-teal-50/40'
                              : 'border-stone-200 hover:border-stone-300'
                          }`}
                          data-testid={`print-option-${p.product_uid}`}
                        >
                          <div>
                            <div className="font-medium text-stone-900 text-sm">{p.title}</div>
                            <div className="text-xs text-stone-500 mt-0.5">{p.size} · {p.paper}</div>
                          </div>
                          <div className="text-teal-700 font-semibold">${p.price_usd.toFixed(2)}</div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-3 mt-4">
              <Label className="text-stone-700">Ship to</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Full name" value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="print-ship-name" />
                <Input placeholder="Email" value={form.customer_email}
                  onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="print-ship-email" />
              </div>
              <Input placeholder="Address line 1" value={form.address_line1}
                onChange={(e) => setForm({ ...form, address_line1: e.target.value })} data-testid="print-ship-addr1" />
              <Input placeholder="Address line 2 (optional)" value={form.address_line2}
                onChange={(e) => setForm({ ...form, address_line2: e.target.value })} />
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Input placeholder="City" value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="print-ship-city" />
                <Input placeholder="State" value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })} />
                <Input placeholder="Postal code" value={form.postal_code}
                  onChange={(e) => setForm({ ...form, postal_code: e.target.value })} data-testid="print-ship-zip" />
                <Input placeholder="Country (ISO)" value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })} maxLength={2} />
              </div>
              <Input placeholder="Phone (optional)" value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">
                Cancel
              </Button>
              <Button onClick={submit} disabled={submitting || !selected}
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"
                data-testid="place-photo-order-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {selected ? `Pay $${selected.price_usd.toFixed(2)} →` : 'Order'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
